import React, { useState, useEffect } from 'react';
import { Mail, Phone, User, FileText, MapPin, Briefcase, ShieldCheck, Landmark, Download, Eye, AlertCircle, Building2, Users, UserPlus, Check, X, ArrowUpCircle } from "lucide-react";
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { referralService } from '../../services/api/referral.service';
import { toast } from 'react-toastify';
import { getCurrentApiUrl } from '../../config/server.config';

// Utility function to format dates
const formatDate = (date: any): string => {
  if (!date) return 'N/A';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'N/A';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return 'N/A';
  }
};

// Document thumbnail component
const DocumentThumbnail = ({ doc, title }: { doc: any; title: string }) => {
  if (!doc) return null;

  const isImage = doc.mimeType?.startsWith('image/');
  const isPDF = doc.mimeType === 'application/pdf';
  
  const handleView = () => {
    if (doc.url) {
      window.open(doc.url, '_blank');
    }
  };
  
  const handleDownload = async () => {
    if (doc.url) {
      // For images, use a different approach
      if (isImage) {
        try {
          // Create an image element
          const img = new Image();
          img.crossOrigin = 'anonymous';
          
          img.onload = () => {
            // Create canvas
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            
            // Draw image on canvas
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0);
            
            // Convert to blob and download
            canvas.toBlob((blob) => {
              if (blob) {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `${title || 'image'}.${doc.mimeType?.split('/')[1] || 'png'}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
              }
            }, doc.mimeType || 'image/png');
          };
          
          img.onerror = () => {
            // If canvas approach fails, try direct download with fetch
            fetch(doc.url)
              .then(response => response.blob())
              .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `${title || 'image'}.${doc.mimeType?.split('/')[1] || 'png'}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
              })
              .catch(() => {
                // Last resort: create a link with download attribute
                const link = document.createElement('a');
                link.href = doc.url;
                link.download = `${title || 'image'}.${doc.mimeType?.split('/')[1] || 'png'}`;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              });
          };
          
          img.src = doc.url;
        } catch (error) {
          console.error('Image download failed:', error);
          // Fallback for images
          const link = document.createElement('a');
          link.href = doc.url;
          link.download = `${title || 'image'}.${doc.mimeType?.split('/')[1] || 'png'}`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        // For PDFs and other documents
        try {
          const response = await fetch(doc.url);
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = title || 'document';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
          // Fallback for documents
          const link = document.createElement('a');
          link.href = doc.url;
          link.download = title || 'document';
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
      <div className="relative">
        {isImage && doc.url ? (
          <img 
            src={doc.url} 
            alt={title}
            className="w-full h-32 object-cover rounded border border-gray-300"
          />
        ) : (
          <div className="w-full h-32 bg-gray-200 rounded border border-gray-300 flex items-center justify-center">
            <FileText className="w-12 h-12 text-gray-400" />
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleView}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm font-medium"
        >
          <Eye className="w-4 h-4" />
          View
        </button>
        <button
          onClick={handleDownload}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm font-medium"
        >
          <Download className="w-4 h-4" />
          Download
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Uploaded: {formatDate(doc.createdAt || doc.uploadedAt)}
      </p>
    </div>
  );
};

const Profile = () => {
  const navigate = useNavigate();
  const authUser = useSelector((state: RootState) => state.auth?.user) as any;
  const [profileData, setProfileData] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);
  const [corporateDocuments, setCorporateDocuments] = useState<any>({ company: [], representatives: [] });
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Referral code application state
  const [showApplyReferralModal, setShowApplyReferralModal] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [applyingReferralCode, setApplyingReferralCode] = useState(false);
  const [referralStatus, setReferralStatus] = useState<any>(null);

  // Fetch user profile from API
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const apiURL = getCurrentApiUrl();

        const response = await axios.get(`${apiURL}/profile/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          console.log('Profile API Response:', response.data.data);
          setProfileData(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        // Fallback to Redux data if API fails
        setProfileData(authUser?.data);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [authUser]);

  // Use profileData if loaded, otherwise fallback to Redux data
  const data = profileData || authUser?.data;

  // Fetch documents from API
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!data) return;
      
      setLoadingDocs(true);
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const apiURL = getCurrentApiUrl();

        const response = await axios.get(`${apiURL}/profile/documents`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          const responseData = response.data.data;
          console.log('Documents API Response:', responseData);
          
          // Handle different account types
          if (responseData.accountType === 'CORPORATE' && responseData.categorized) {
            setCorporateDocuments(responseData.categorized);
            setDocuments(responseData.documents || []);
          } else {
            setDocuments(responseData.documents || []);
          }
          console.log('Documents set:', responseData.documents);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setLoadingDocs(false);
      }
    };
    
    fetchDocuments();
  }, [data]);

  // Fetch referral status on component mount
  useEffect(() => {
    const fetchReferralStatus = async () => {
      try {
        const status = await referralService.getMyStatus();
        setReferralStatus(status);
      } catch (error) {
        console.error('Error fetching referral status:', error);
      }
    };

    if (data) {
      fetchReferralStatus();
    }
  }, [data]);

  // Handle referral code application
  const handleApplyReferralCode = async () => {
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    setApplyingReferralCode(true);
    try {
      // Validate code first
      const response = await referralService.validateCode(referralCode);
      const validation = response.data || response; // Handle both response formats
      if (!validation.valid) {
        toast.error(validation.message || 'Invalid referral code');
        return;
      }

      // Apply the code
      await referralService.applyReferralCode(referralCode);
      toast.success('Referral code applied successfully!');

      // Refresh referral status
      const updatedStatus = await referralService.getMyStatus();
      setReferralStatus(updatedStatus);

      // Close modal and reset form
      setShowApplyReferralModal(false);
      setReferralCode('');

      // Optionally refresh the entire user data to show updated referrer info
      setTimeout(() => window.location.reload(), 1500);

    } catch (error: any) {
      toast.error(error.message || 'Error applying referral code');
    } finally {
      setApplyingReferralCode(false);
    }
  };

  // Handle upgrade to INDIVIDUAL account
  const handleUpgradeToIndividual = () => {
    console.log('🔄 UPGRADE: Iniciando proceso de upgrade');

    // Guardar flag de upgrade en localStorage para que el onboarding lo detecte
    localStorage.setItem('upgradeFromExpress', 'true');
    console.log('🔄 UPGRADE: Flag guardado en localStorage:', localStorage.getItem('upgradeFromExpress'));

    toast.info('Starting upgrade to INDIVIDUAL account...');

    // Redirigir al onboarding
    console.log('🔄 UPGRADE: Redirigiendo a /onboarding');
    navigate('/onboarding');
  };
  
  // Add null/undefined checks
  if (loadingProfile || !data) {
    return (
      <div className="max-w-7xl mx-auto animate-fadeIn p-6">
        <div className="bg-white rounded-xl p-10 shadow-[0_20px_40px_rgba(0,0,0,0.1)]">
          <p className="text-gray-600">Loading profile data...</p>
        </div>
      </div>
    );
  }

  // Determine account type
  const accountType = data.accountType || 'EXPRESS';
  
  // Create initials safely for all account types
  const getInitials = () => {
    if (accountType === 'EXPRESS' && data.onboardingExpress) {
      const first = data.onboardingExpress.firstName?.[0]?.toUpperCase() || '';
      const last = data.onboardingExpress.lastName?.[0]?.toUpperCase() || '';
      return first + last || 'U';
    } else if (accountType === 'INDIVIDUAL' && data.onboardingIndividual) {
      const first = data.onboardingIndividual.firstName?.[0]?.toUpperCase() || '';
      const last = data.onboardingIndividual.lastName?.[0]?.toUpperCase() || '';
      return first + last || 'U';
    } else if (accountType === 'CORPORATE' && (data.corporateInfo || data.onboardingCorporate)) {
      const companyName = data.corporateInfo?.companyName || data.onboardingCorporate?.companyName;
      return companyName?.[0]?.toUpperCase() || 'C';
    } else if (data.profile) {
      const first = data.profile.firstName?.[0]?.toUpperCase() || '';
      const last = data.profile.lastName?.[0]?.toUpperCase() || '';
      return first + last || 'U';
    }
    return 'U';
  };

  // Get full name based on account type
  const getFullName = () => {
    if (accountType === 'EXPRESS' && data.onboardingExpress) {
      return `${data.onboardingExpress.firstName || ''} ${data.onboardingExpress.lastName || ''}`.trim();
    } else if (accountType === 'INDIVIDUAL' && data.onboardingIndividual) {
      return `${data.onboardingIndividual.firstName || ''} ${data.onboardingIndividual.lastName || ''}`.trim();
    } else if (accountType === 'CORPORATE' && (data.corporateInfo || data.onboardingCorporate)) {
      return data.corporateInfo?.companyName || data.onboardingCorporate?.companyName || 'Company';
    } else if (data.profile) {
      return data.profile.fullName || 'User';
    }
    return 'User';
  };

  const initials = getInitials();
  const fullName = getFullName();

  // Render EXPRESS account view
  if (accountType === 'EXPRESS') {
    return (
      <div className="max-w-7xl mx-auto animate-fadeIn">
        <div className="bg-white rounded-xl p-10 shadow-[0_20px_40px_rgba(0,0,0,0.1)] mb-6">
          <div className='flex items-center gap-[35px] flex-wrap'>
            <div className="relative flex-shrink-0">
              <div className="w-[130px] h-[130px] rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] flex items-center justify-center text-white text-5xl font-bold shadow-[0_10px_25px_rgba(102,126,234,0.4)] transition-transform duration-300 ease-in-out hover:scale-[1.05]">
                {initials}
              </div>
              <div className='absolute bottom-2 right-2 bg-emerald-500 w-8 h-8 rounded-full border-4 border-white shadow-[0_2px_8px_rgba(16,185,129,0.4)] animate-pulse-custom hover:scale-[1.09922]'></div>
            </div>
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-gray-800 mb-[10px] leading-tight">{fullName}</h1>
              <span className="inline-block bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 px-4 py-3 rounded-[25px] text-[14px] font-semibold mb-3 uppercase tracking-[0.5px]">EXPRESS Account</span>
              <div className="flex gap-[30px] flex-wrap">
                <div className='flex items-center gap-[10px] text-gray-600 text-[15px]'>
                  <Mail className='w-[25px] h-[25px] text-[#666666]'/>
                  <span className="text-gray-600">{data.email}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Upgrade to INDIVIDUAL Button */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6 shadow-md">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUpCircle className="w-6 h-6 text-purple-600" />
                <h3 className="text-lg font-bold text-gray-800">Upgrade to INDIVIDUAL Account</h3>
              </div>
              <p className="text-sm text-gray-700 mb-4">
                Unlock advanced features including crypto wallets, MasterCard issuance, higher transaction limits, and full KYC verification.
              </p>
              <button
                onClick={handleUpgradeToIndividual}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <ArrowUpCircle className="w-5 h-5" />
                Start Upgrade Process
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render CORPORATE account view
  if (accountType === 'CORPORATE' && (data.corporateInfo || data.onboardingCorporate)) {
    const corp = data.corporateInfo || data.onboardingCorporate;
    const representatives = data.representatives || data.onboardingCorporate?.representatives || [];

    return (
      <div className="max-w-7xl mx-auto animate-fadeIn">
        {/* Header */}
        <div className="bg-white rounded-xl p-10 mb-[30px] shadow-[0_20px_40px_rgba(0,0,0,0.1)]">
          <div className='flex items-center gap-[35px] flex-wrap'>
            <div className="relative flex-shrink-0">
              <div className="w-[130px] h-[130px] rounded-full bg-gradient-to-br from-[#059669] to-[#047857] flex items-center justify-center text-white text-5xl font-bold shadow-[0_10px_25px_rgba(5,150,105,0.4)] transition-transform duration-300 ease-in-out hover:scale-[1.05]">
                {initials}
              </div>
              <div className='absolute bottom-2 right-2 bg-emerald-500 w-8 h-8 rounded-full border-4 border-white shadow-[0_2px_8px_rgba(16,185,129,0.4)] animate-pulse-custom hover:scale-[1.09922]'></div>
            </div>
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-gray-800 mb-[10px] leading-tight">{corp.companyName}</h1>
              <span className="inline-block bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-700 px-4 py-3 rounded-[25px] text-[14px] font-semibold mb-3 uppercase tracking-[0.5px]">CORPORATE Account</span>
              <div className="flex gap-[30px] flex-wrap">
                <div className='flex items-center gap-[10px] text-gray-600 text-[15px]'>
                  <Mail className='w-[25px] h-[25px] text-[#666666]'/>
                  <span className="text-gray-600">{corp.companyEmail || data.email}</span>
                </div>
                {(corp.companyPhoneCountryCode && corp.companyPhoneNumber) && (
                  <div className='flex items-center gap-[10px] text-gray-600 text-[15px]'>
                    <Phone className='w-[25px] h-[25px] text-[#666666]'/>
                    <span className="text-gray-600">{corp.companyPhoneCountryCode} {corp.companyPhoneNumber}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Datos Básicos de la Empresa */}
        <div className="bg-white rounded-[16px] p-[28px] mb-6 shadow-[0_10px_25px_rgba(0,0,0,0.08)]">
          <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
            <Building2 className='w-[25px] h-[25px] text-[#666666]'/>
            <span>Datos Básicos de la Empresa</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Nombre Comercial:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.companyName || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Nombre Legal:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.legalName || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Tipo de Entidad:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.companyType || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Email Empresa:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.companyEmail || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Código País Teléfono:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.companyPhoneCountryCode || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Número Teléfono:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.companyPhoneNumber || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb] md:col-span-2'>
              <span className='text-gray-500 text-[14px] font-medium'>Website:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.website || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Registro e Incorporación + Actividad Empresarial + Dirección */}
        <div className="grid gap-[25px] [grid-template-columns:repeat(auto-fit,minmax(350px,1fr))]">
          {/* Registro e Incorporación */}
          <div className="bg-white rounded-[16px] p-[28px] shadow-[0_10px_25px_rgba(0,0,0,0.08)]">
            <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
              <FileText className='w-[25px] h-[25px] text-[#666666]'/>
              <span>Registro e Incorporación</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Tax ID:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.taxId || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>País Tax:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.taxCountry || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Número de Registro:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.registrationNumber || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>País de Registro:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.registrationCountry || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Fecha de Registro:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{formatDate(corp.registrationDate)}</span>
            </div>
            <div className='flex justify-between items-center py-[12px]'>
              <span className='text-gray-500 text-[14px] font-medium'>Fecha de Incorporación:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{formatDate(corp.incorporationDate)}</span>
            </div>
          </div>

          {/* Actividad Empresarial */}
          <div className="bg-white rounded-[16px] p-[28px] shadow-[0_10px_25px_rgba(0,0,0,0.08)]">
            <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
              <Briefcase className='w-[25px] h-[25px] text-[#666666]'/>
              <span>Actividad Empresarial</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Código Sector:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.businessSectorCode || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Actividad:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.businessActivity || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Tipo de Negocio:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.businessType || 'N/A'}</span>
            </div>
            {corp.businessDescription && (
              <div className='py-[12px]'>
                <span className='text-gray-500 text-[14px] font-medium block mb-2'>Descripción:</span>
                <p className='text-gray-800 text-[13px] leading-relaxed'>{corp.businessDescription}</p>
              </div>
            )}
          </div>

          {/* Dirección de la Empresa */}
          <div className="bg-white rounded-[16px] p-[28px] shadow-[0_10px_25px_rgba(0,0,0,0.08)]">
            <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
              <MapPin className='w-[25px] h-[25px] text-[#666666]'/>
              <span>Dirección de la Empresa</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Dirección 1:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.addressLine1 || corp.operationalAddressLine1 || corp.registeredAddressLine1 || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Dirección 2:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.addressLine2 || corp.operationalAddressLine2 || corp.registeredAddressLine2 || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Ciudad:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.city || corp.operationalCity || corp.registeredCity || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Estado/Provincia:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.state || corp.operationalState || corp.registeredState || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px] border-b border-[#f9fafb]'>
              <span className='text-gray-500 text-[14px] font-medium'>Código Postal:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.postalCode || corp.operationalPostalCode || corp.registeredPostalCode || 'N/A'}</span>
            </div>
            <div className='flex justify-between items-center py-[12px]'>
              <span className='text-gray-500 text-[14px] font-medium'>País:</span>
              <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{corp.country || corp.operationalCountry || corp.registeredCountry || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Account Status */}
        <div className="bg-white rounded-[16px] p-[28px] mt-6 shadow-[0_10px_25px_rgba(0,0,0,0.08)] transition-transform duration-300 ease-in-out hover:translate-y-[-5px] hover:shadow-[0_15px_35px_rgba(0,0,0,0.12)]">
          <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
            <ShieldCheck className='w-[25px] h-[25px] text-[#666666]'/>
            <span>Account Status</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Account type:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>CORPORATE</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Status:</span>
            <span className='text-green-600 text-[14px] font-semibold text-right max-w-[60%]'>{data.user?.accountStatus || 'ACTIVE'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Registration date:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{formatDate(data.user?.createdAt)}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Referred by:</span>
            <div className="flex items-center gap-2">
              {referralStatus?.referredBy ? (
                <span className='text-gray-800 text-[14px] font-semibold text-right'>
                  {referralStatus.referredBy.name} ({referralStatus.referredBy.email})
                </span>
              ) : data.referralInfo?.referrerName ? (
                <span className='text-gray-800 text-[14px] font-semibold text-right'>
                  {data.referralInfo.referrerName}
                </span>
              ) : (
                <>
                  <span className='text-gray-500 text-[14px] text-right'>Not referred</span>
                  <button
                    onClick={() => setShowApplyReferralModal(true)}
                    className="ml-2 flex items-center gap-1 px-3 py-1 bg-blue-500 text-white text-xs rounded-md hover:bg-blue-600 transition-colors"
                    title="Apply referral code"
                  >
                    <UserPlus className="w-3 h-3" />
                    Apply Code
                  </button>
                </>
              )}
            </div>
          </div>
          <div className='flex justify-between items-center py-[14px] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Onboarding completed:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>
              {data.user?.onboardingCompletedAt ? formatDate(data.user.onboardingCompletedAt) : 'Pending'}
            </span>
          </div>
        </div>

        {/* Company Documents - ONLY show company documents, exclude representative docs */}
        {(() => {
          // Filter out representative documents using corporateRepresentativeId field from schema
          console.log('🔍 ALL company array docs:', corporateDocuments.company);
          const companyOnlyDocs = corporateDocuments.company.filter((doc: any) => {
            // If document has corporateRepresentativeId, it belongs to a representative, not the company
            const isCompanyDoc = !doc.corporateRepresentativeId;
            console.log(`📄 Doc ${doc.id} - corporateRepresentativeId:`, doc.corporateRepresentativeId, '- isCompanyDoc:', isCompanyDoc);
            return isCompanyDoc;
          });
          console.log('✅ Filtered COMPANY ONLY docs:', companyOnlyDocs);

          return (companyOnlyDocs.length > 0 || loadingDocs) && (
            <div className="bg-white rounded-[16px] p-[28px] mt-6 shadow-[0_10px_25px_rgba(0,0,0,0.08)]">
              <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
                <FileText className='w-[25px] h-[25px] text-[#666666]'/>
                <span>Company Documents</span>
              </div>
              {loadingDocs ? (
                <p className="text-gray-500 text-center py-8">Loading documents...</p>
              ) : companyOnlyDocs.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {companyOnlyDocs.slice().reverse().map((doc: any, idx: number) => (
                    <DocumentThumbnail key={doc.id || idx} doc={doc} title={doc.documentType || `Document ${idx + 1}`} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No company documents uploaded</p>
              )}
            </div>
          );
        })()}

        {/* Representatives */}
        {representatives.length > 0 && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <Users className="w-7 h-7" />
              Company Representatives
            </h2>
            {representatives
              .filter((rep: any, index: number, array: any[]) => {
                // Filter out incomplete entries
                if (!rep.firstName || !rep.lastName) return false;
                
                // Remove duplicates - keep only the one with more complete data
                const duplicates = array.filter(r => 
                  r.firstName === rep.firstName && 
                  r.lastName === rep.lastName
                );
                
                if (duplicates.length > 1) {
                  // If there are duplicates, keep the one with documentNumber filled
                  // or the one that appears last (usually more complete)
                  const bestVersion = duplicates.reduce((best, current) => {
                    // Prefer the one with a non-empty documentNumber
                    if (current.documentNumber && !best.documentNumber) return current;
                    if (!current.documentNumber && best.documentNumber) return best;
                    // Otherwise prefer the one that appears later (usually more complete)
                    return array.indexOf(current) > array.indexOf(best) ? current : best;
                  });
                  return rep === bestVersion;
                }
                
                return true;
              })
              .map((rep: any, index: number) => (
              <div key={rep.id || index} className="bg-white rounded-[16px] p-[28px] mb-6 shadow-[0_10px_25px_rgba(0,0,0,0.08)]">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {rep.firstName} {rep.lastName} - {rep.role || 'Representative'}
                </h3>
                <div className="grid gap-[25px] [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
                  {/* Personal Information */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Personal Information</h4>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Full Name:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>
                        {`${rep.firstName || ''} ${rep.middleName ? rep.middleName + ' ' : ''}${rep.lastName || ''}${rep.secondLastName ? ' ' + rep.secondLastName : ''}`.trim()}
                      </span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Gender:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.gender || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Birth Date:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{formatDate(rep.birthDate)}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Email:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.email || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Phone:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>
                        {rep.phoneCountryCode && rep.phoneNumber ? `${rep.phoneCountryCode} ${rep.phoneNumber}` : rep.phone || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Nacionalidad y Ciudadanía */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Nacionalidad y Ciudadanía</h4>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Nacionalidad:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.nationality || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Segunda Nacionalidad:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.secondNationality || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Ciudadanía:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.citizenship || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Documento de Identidad */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Documento de Identidad</h4>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Tipo de Documento:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.documentType || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Número de Documento:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.documentNumber || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>País del Documento:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.documentCountry || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Fecha de Expiración:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{formatDate(rep.documentExpiry)}</span>
                    </div>
                  </div>

                  {/* Dirección */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Dirección</h4>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Dirección 1:</span>
                      <span className='text-gray-800 text-[13px] font-semibold text-right max-w-[60%]'>{rep.addressLine1 || rep.currentAddress || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Dirección 2:</span>
                      <span className='text-gray-800 text-[13px] font-semibold text-right max-w-[60%]'>{rep.addressLine2 || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Ciudad:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.city || rep.currentCity || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Estado/Provincia:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.state || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Código Postal:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.postalCode || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>País:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.country || rep.currentCountry || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Información Fiscal */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Información Fiscal</h4>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>País Fiscal:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.taxCountry || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Número Fiscal:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.taxNumber || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Relación con la Empresa */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Relación con la Empresa</h4>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Tipo de Relación:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.relationshipType || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Rol:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.role || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Porcentaje de Propiedad:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.ownershipPercentage ? `${rep.ownershipPercentage}%` : 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Beneficiario Final:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.isUltimateBeneficiary ? 'Sí' : 'No'}</span>
                    </div>
                    {rep.powerOfAttorneyNumber && (
                      <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                        <span className='text-gray-500 text-[13px] font-medium'>Número de Poder:</span>
                        <span className='text-gray-800 text-[13px] font-semibold'>{rep.powerOfAttorneyNumber}</span>
                      </div>
                    )}
                    {rep.powerOfAttorneyDate && (
                      <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                        <span className='text-gray-500 text-[13px] font-medium'>Fecha de Poder:</span>
                        <span className='text-gray-800 text-[13px] font-semibold'>{formatDate(rep.powerOfAttorneyDate)}</span>
                      </div>
                    )}
                    {rep.representationStartDate && (
                      <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                        <span className='text-gray-500 text-[13px] font-medium'>Inicio de Representación:</span>
                        <span className='text-gray-800 text-[13px] font-semibold'>{formatDate(rep.representationStartDate)}</span>
                      </div>
                    )}
                  </div>

                  {/* Empleo e Ingresos */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Empleo e Ingresos</h4>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Estado de Empleo:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.employmentStatus || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Rango de Ingresos:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.monthlyIncomeRange || 'N/A'}</span>
                    </div>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Fuente de Ingresos:</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.incomeSource || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Compliance */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-3">Compliance</h4>
                    <div className='flex justify-between items-center py-[10px] border-b border-[#f9fafb]'>
                      <span className='text-gray-500 text-[13px] font-medium'>Persona Políticamente Expuesta (PEP):</span>
                      <span className='text-gray-800 text-[13px] font-semibold'>{rep.isPEP ? 'Sí' : 'No'}</span>
                    </div>
                  </div>
                </div>

                {/* Show representative's documents from the API response - BELOW the grid */}
                {(() => {
                  const repDocs = corporateDocuments.representatives.filter(
                    (doc: any) => doc.representativeName === `${rep.firstName} ${rep.lastName}`
                  );
                  return repDocs.length > 0 ? (
                    <div className="mt-6 border-t border-gray-200 pt-6">
                      <h4 className="font-medium text-gray-700 mb-4 text-base">Documentos del Representante</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {repDocs.slice().reverse().map((doc: any, dIdx: number) => (
                          <DocumentThumbnail key={doc.id || dIdx} doc={doc} title={doc.documentType || `Doc ${dIdx + 1}`} />
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            ))}
          </div>
        )}

        {/* Information Message at bottom */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-700">
                To update any of your company or representative information, please open a support ticket.
              </p>
              <Link to="/dashboard/support" className="text-sm text-blue-600 hover:text-blue-800 underline mt-1 inline-block">
                Go to Support →
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default to INDIVIDUAL account view (most detailed)
  const individual = data.onboardingIndividual || data.profile || {};

  return (
    <div className="max-w-7xl mx-auto animate-fadeIn">
      <div className="bg-white rounded-xl p-10 mb-[30px] shadow-[0_20px_40px_rgba(0,0,0,0.1)]">
        <div className='flex items-center gap-[35px] flex-wrap'>
          <div className="relative flex-shrink-0">
            <div className="w-[130px] h-[130px] rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] flex items-center justify-center text-white text-5xl font-bold shadow-[0_10px_25px_rgba(102,126,234,0.4)] transition-transform duration-300 ease-in-out hover:scale-[1.05]">
              {initials}
            </div>
            <div className='absolute bottom-2 right-2 bg-emerald-500 w-8 h-8 rounded-full border-4 border-white shadow-[0_2px_8px_rgba(16,185,129,0.4)] animate-pulse-custom hover:scale-[1.09922]'></div>
          </div>
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-gray-800 mb-[10px] leading-tight">{fullName}</h1>
            <span className="inline-block bg-gradient-to-br from-gray-100 to-gray-200 text-gray-500 px-4 py-3 rounded-[25px] text-[14px] font-semibold mb-3 uppercase tracking-[0.5px]">Verified Client</span>
            <div className="flex gap-[30px] flex-wrap">
              <div className='flex items-center gap-[10px] text-gray-600 text-[15px]'>
                <Mail className='w-[25px] h-[25px] text-[#666666]'/>
                <span className="text-gray-600">{data.email}</span>
              </div>
              {individual.phone && (
                <div className='flex items-center gap-[10px] text-gray-600 text-[15px]'>
                  <Phone className='w-[25px] h-[25px] text-[#666666]'/>
                  <span className="text-gray-600">{individual.phone}</span>
                </div>
              )}
              {individual.tier && (
                <div className='flex items-center gap-[10px] text-gray-600 text-[15px]'>
                  <span className="bg-gradient-to-br from-yellow-600 to-yellow-500 inline-flex items-center gap-[6px] text-white px-[16px] py-[6px] rounded-[15px] text-[12px] font-bold uppercase tracking-[0.5px] shadow-[0_4px_12px_rgba(245,158,11,0.3)]">
                    ⭐ TIER {individual.tier}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


      <div className="grid gap-[25px] [grid-template-columns:repeat(auto-fit,minmax(350px,1fr))]">
        {/* Personal Information */}
        <div className="bg-white rounded-[16px] p-[28px] shadow-[0_10px_25px_rgba(0,0,0,0.08)] transition-transform duration-300 ease-in-out hover:translate-y-[-5px] hover:shadow-[0_15px_35px_rgba(0,0,0,0.12)]">
          <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
            <User className='w-[25px] h-[25px] text-[#666666]'/>
            <span>Personal Information</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Full name:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{fullName}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Birthdate:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{formatDate(individual.birthDate)}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Gender:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.gender === "M" ? "Male" : individual.gender === "F" ? "Female" : individual.gender || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Nationality:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.nationality || individual.nationalities?.join(', ') || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Country of birth:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.birthCountry || 'N/A'}</span>
          </div>
        </div>

        {/* Documentation */}
        <div className="bg-white rounded-[16px] p-[28px] shadow-[0_10px_25px_rgba(0,0,0,0.08)] transition-transform duration-300 ease-in-out hover:translate-y-[-5px] hover:shadow-[0_15px_35px_rgba(0,0,0,0.12)]">
          <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
            <FileText className='w-[25px] h-[25px] text-[#666666]'/>
            <span>Documentation</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Document type:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.documentType || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Number:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.documentNumber || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Issuing country:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.documentCountry || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Expiration date:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{formatDate(individual.documentExpiry)}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Tax Number:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.taxNumber || 'N/A'}</span>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-[16px] p-[28px] shadow-[0_10px_25px_rgba(0,0,0,0.08)] transition-transform duration-300 ease-in-out hover:translate-y-[-5px] hover:shadow-[0_15px_35px_rgba(0,0,0,0.12)]">
          <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
            <MapPin className='w-[25px] h-[25px] text-[#666666]'/>
            <span>Address</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Address Line 1:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.addressLine1 || individual.address || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Address Line 2:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.addressLine2 || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>City:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.city || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>State/Province:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.state || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Postal code:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.postalCode || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Country:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.country || 'N/A'}</span>
          </div>
        </div>

        {/* Employment Information */}
        <div className="bg-white rounded-[16px] p-[28px] shadow-[0_10px_25px_rgba(0,0,0,0.08)] transition-transform duration-300 ease-in-out hover:translate-y-[-5px] hover:shadow-[0_15px_35px_rgba(0,0,0,0.12)]">
          <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
            <Briefcase className='w-[25px] h-[25px] text-[#666666]'/>
            <span>Employment Information</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Occupation:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.occupation || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Employment status:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.employmentStatus || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Employer:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.employerName || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Position:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.jobTitle || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Monthly income:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.monthlyIncomeRange || individual.monthlyIncome || 'N/A'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Source of income:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{individual.incomeSource || 'N/A'}</span>
          </div>
        </div>

        {/* Account Status */}
        <div className="bg-white rounded-[16px] p-[28px] shadow-[0_10px_25px_rgba(0,0,0,0.08)] transition-transform duration-300 ease-in-out hover:translate-y-[-5px] hover:shadow-[0_15px_35px_rgba(0,0,0,0.12)]">
          <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
            <ShieldCheck className='w-[25px] h-[25px] text-[#666666]'/>
            <span>Account Status</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Account type:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>{data.accountType || 'INDIVIDUAL'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Status:</span>
            <span className='text-green-600 text-[14px] font-semibold text-right max-w-[60%]'>{data.accountStatus || 'ACTIVE'}</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Email verified:</span>
            <span className='text-green-600 text-[14px] font-semibold text-right max-w-[60%]'>Yes</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>2FA enabled:</span>
            <span className={`${!data.isTwoFactorEnabled ? 'text-red-600' : 'text-green-600'} text-[14px] font-semibold text-right max-w-[60%]`}>
              {data.isTwoFactorEnabled ? "Yes" : "No"}
            </span>
          </div>
          <div className='flex justify-between items-center py-[14px] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Referred by:</span>
            <div className="flex items-center gap-2">
              {referralStatus?.referredBy ? (
                <span className='text-gray-800 text-[14px] font-semibold text-right'>
                  {referralStatus.referredBy.name} ({referralStatus.referredBy.email})
                </span>
              ) : data.referralInfo?.referrerName ? (
                <span className='text-gray-800 text-[14px] font-semibold text-right'>
                  {data.referralInfo.referrerName}
                </span>
              ) : (
                <>
                  <span className='text-gray-500 text-[14px] text-right'>Not referred</span>
                  <button
                    onClick={() => setShowApplyReferralModal(true)}
                    className="ml-2 flex items-center gap-1 px-3 py-1 bg-blue-500 text-white text-xs rounded-md hover:bg-blue-600 transition-colors"
                    title="Apply referral code"
                  >
                    <UserPlus className="w-3 h-3" />
                    Apply Code
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tax Residency */}
        <div className="bg-white rounded-[16px] p-[28px] shadow-[0_10px_25px_rgba(0,0,0,0.08)] transition-transform duration-300 ease-in-out hover:translate-y-[-5px] hover:shadow-[0_15px_35px_rgba(0,0,0,0.12)]">
          <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
            <Landmark className='w-[25px] h-[25px] text-[#666666]'/>
            <span>Tax Residence</span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Current residence:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>
              {individual.currentResidence || individual.country || 'N/A'}
            </span>
          </div>
          <div className='flex justify-between items-center py-[14px] border-b border-[#f9fafb] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>Tax residency:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>
              {individual.taxResidence || individual.taxCountry || 'N/A'}
            </span>
          </div>
          <div className='flex justify-between items-center py-[14px] transition-colors duration-200 ease-in-out'>
            <span className='text-gray-500 text-[14px] font-medium'>US Residency Status:</span>
            <span className='text-gray-800 text-[14px] font-semibold text-right max-w-[60%]'>
              {(data.usResidencyStatus || individual.usResidencyStatus) === "citizen" ? "US Citizen" :
               (data.usResidencyStatus || individual.usResidencyStatus) === "non-resident" ? "Non-resident" :
               (data.usResidencyStatus || individual.usResidencyStatus) ? (data.usResidencyStatus || individual.usResidencyStatus).charAt(0).toUpperCase() + (data.usResidencyStatus || individual.usResidencyStatus).slice(1) : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Documents Section - Show after information cards */}
      {(documents.length > 0 || loadingDocs) && (
        <div className="bg-white rounded-[16px] p-[28px] mt-6 shadow-[0_10px_25px_rgba(0,0,0,0.08)]">
          <div className="flex items-center gap-[12px] text-[20px] font-semibold text-gray-800 mb-[24px] pb-[12px] border-b-2 border-gray-100">
            <FileText className='w-[25px] h-[25px] text-[#666666]'/>
            <span>Uploaded Documents</span>
          </div>
          {loadingDocs ? (
            <p className="text-gray-500 text-center py-8">Loading documents...</p>
          ) : documents.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {documents.slice().reverse().map((doc: any, idx: number) => (
                <DocumentThumbnail key={doc.id || idx} doc={doc} title={doc.documentType || `Document ${idx + 1}`} />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No documents uploaded</p>
          )}
        </div>
      )}

      {/* Information Message at bottom */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-6">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm text-blue-700">
              To update any of your personal information, please open a support ticket.
            </p>
            <Link to="/dashboard/support" className="text-sm text-blue-600 hover:text-blue-800 underline mt-1 inline-block">
              Go to Support →
            </Link>
          </div>
        </div>
      </div>

      {/* Apply Referral Code Modal */}
      {showApplyReferralModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Apply Referral Code</h3>
              <button
                onClick={() => {
                  setShowApplyReferralModal(false);
                  setReferralCode('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <p className="text-gray-600 mb-4">
              Enter a referral code to connect with someone who referred you to BELOBABA.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Referral Code
              </label>
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="Enter referral code"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase font-mono"
                disabled={applyingReferralCode}
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowApplyReferralModal(false);
                  setReferralCode('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={applyingReferralCode}
              >
                Cancel
              </button>
              <button
                onClick={handleApplyReferralCode}
                disabled={applyingReferralCode || !referralCode.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {applyingReferralCode ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Applying...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Apply Code
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
